import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Award, Users, Sparkles, Phone } from "lucide-react";
import Marquee from "react-fast-marquee";
import { SERVICES, PROCESS_STEPS, STATS } from "../lib/siteData";
import Testimonials from "../components/Testimonials";
import PartnerMarquee from "../components/PartnerMarquee";
import CTABanner from "../components/CTABanner";
import HeroLeadCard from "../components/HeroLeadCard";
import SEO from "../components/SEO";

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <SEO />
      {/* HERO */}
      <section
        className="relative min-h-[88vh] flex items-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.72), rgba(29,78,216,0.55)), url(https://static.prod-images.emergentagent.com/jobs/af236e4b-6d37-4c12-98e2-7d0d973c0359/images/2d338e8616b43ab68b9bf7187644063049ab7f548d3560a22466f12f6e78285a.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container-narrow py-16 lg:py-28 grid lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6 backdrop-blur">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" /> India's trusted solar brand — since 2021
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.05] mb-5">
              Switch to <span className="text-yellow-400">Smart Solar Energy</span>
            </h1>
            <p className="text-lg text-slate-200 max-w-xl mb-7 leading-relaxed">
              Cut your electricity bill by up to 90%. Enjoy 25-year warranty, subsidy up to ₹78,000 and zero hassle installation by MNRE-approved experts.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Link to="/contact" className="btn-primary" data-testid="hero-get-consultation-btn">
                Get Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/calculator" className="btn-outline bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="hero-calc-btn">
                Calculate Savings
              </Link>
            </div>
            <div className="flex flex-wrap gap-5 text-sm">
              {["Save 90% on bills", "Govt subsidy support", "25-year warranty"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up">
            <HeroLeadCard />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-slate-900 py-8 border-b border-slate-800">
        <Marquee gradient={false} speed={30}>
          {[...STATS, ...STATS].map((s, i) => (
            <div key={i} className="mx-10 flex items-center gap-3 text-white">
              <div className="stat-number text-3xl text-yellow-400">{s.value}</div>
              <div className="text-sm text-slate-400 uppercase tracking-widest">{s.label}</div>
              <div className="w-1 h-1 rounded-full bg-slate-700 ml-4" />
            </div>
          ))}
        </Marquee>
      </section>

      {/* SERVICES BENTO */}
      <section className="section-pad">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-12 gap-6 mb-10 items-end">
            <div className="lg:col-span-7">
              <div className="badge-eco mb-3">What we do</div>
              <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900 tracking-tight">Complete solar solutions, end to end</h2>
            </div>
            <div className="lg:col-span-5 text-slate-600">
              From rooftop residential systems to MW-scale industrial plants — we engineer, procure and install. One partner, zero hassle.
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <Link
                to={`/products/${s.slug}`}
                key={s.slug}
                className="group relative bg-white rounded-2xl border border-slate-200 p-6 hover:border-blue-400 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition">
                  <s.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">{s.summary}</p>
                <div className="flex items-center text-sm font-semibold text-blue-700 group-hover:gap-3 gap-2 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-pad bg-slate-50">
        <div className="container-narrow">
          <div className="text-center mb-14">
            <div className="badge-eco mb-3 mx-auto">Process</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900 tracking-tight">From enquiry to energy in 4 steps</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS_STEPS.map((p, i) => (
              <div key={p.step} className="bg-white rounded-2xl p-6 border border-slate-200 relative">
                <div className="font-heading text-5xl font-bold text-blue-600/20 absolute top-4 right-4">{p.step}</div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section-pad">
        <div className="container-narrow grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="badge-eco mb-3">Why CNGTPL Solar</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900 tracking-tight mb-5">12,000+ happy homes can't be wrong</h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              A decade of solar expertise, 50+ MW installed and a dedicated in-house EPC team — every installation is designed, procured and commissioned by us. No subcontractors, no surprises.
            </p>
            <div className="space-y-3">
              {[
                { icon: ShieldCheck, title: "MNRE Approved Vendor", desc: "Full subsidy eligibility on all residential installations" },
                { icon: Award, title: "Tier-1 Components only", desc: "Waaree, Adani, Vikram, TATA Power — all certified" },
                { icon: Users, title: "In-house design team", desc: "Solar engineers trained for Indian rooftop conditions" },
              ].map((f) => (
                <div key={f.title} className="flex gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-blue-600/10 text-blue-700 flex items-center justify-center">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-slate-900">{f.title}</div>
                    <div className="text-sm text-slate-600">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-outline mt-7" data-testid="why-learn-more-btn">
              More about us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {STATS.map((s, i) => (
              <div key={i} className={`rounded-2xl p-6 ${i % 2 === 0 ? "bg-slate-900 text-white" : "bg-blue-600 text-white"}`}>
                <div className="stat-number text-4xl lg:text-5xl font-bold mb-1">{s.value}</div>
                <div className="text-xs uppercase tracking-widest opacity-80">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PartnerMarquee />
      <Testimonials />
      <CTABanner />
    </div>
  );
}
